import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import type { BusinessClientSummary } from '@/types';

/**
 * Project (business-client) picker — a searchable dropdown that replaces the
 * raw play_service_id text field. With ~hundreds of clients, selecting by name
 * beats typing an opaque 40-char id, and it can't be mistyped.
 */
export function ClientSelect({
  value,
  onChange,
  clients,
  loading,
  error,
}: {
  value: string;
  onChange: (playServiceId: string) => void;
  clients: BusinessClientSummary[];
  loading: boolean;
  error?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = clients.find((c) => c.play_service_id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        c.client_name.toLowerCase().includes(q) ||
        c.play_service_id.toLowerCase().includes(q) ||
        (c.partner ?? '').toLowerCase().includes(q),
    );
  }, [clients, query]);

  // A deep-linked psid that isn't in the list (removed client, or list failed)
  // must still be shown — never silently drop a value the user came in with.
  const label = selected?.client_name ?? (value ? value : loading ? 'Loading projects…' : 'Select project…');

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-[240px] items-center gap-2 border bg-surface-sunken px-2 py-1.5 text-left',
            'hover:border-hairline-strong',
            error ? 'border-crit/50' : 'border-hairline',
            open && 'border-accent',
          )}
        >
          <span className="font-condensed text-[9px] uppercase tracking-wider text-text-faint">
            project
          </span>
          <span
            className={cn(
              'flex-1 truncate font-mono text-[12px]',
              selected || value ? 'text-text' : 'text-text-faint',
            )}
          >
            {label}
          </span>
          {selected?.partner && (
            <span className="shrink-0 border border-hairline px-1 font-condensed text-[8px] uppercase tracking-wider text-text-faint">
              {selected.partner}
            </span>
          )}
          <ChevronsUpDown size={12} className="shrink-0 text-text-faint" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[300px] border border-hairline-strong bg-surface-raised shadow-xl shadow-black/50"
        >
          <div className="flex items-center gap-2 border-b border-hairline px-2">
            <Search size={12} className="shrink-0 text-text-faint" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects…"
              spellCheck={false}
              className="w-full bg-transparent py-2 font-mono text-[12px] text-text placeholder:text-text-faint focus:outline-none"
            />
          </div>

          <div className="max-h-[320px] overflow-auto py-1">
            {loading && <Message>Loading projects…</Message>}
            {error && !loading && <Message tone="crit">Could not load projects.</Message>}
            {!loading && !error && filtered.length === 0 && (
              <Message>No project matches “{query}”.</Message>
            )}

            {filtered.map((c) => {
              const active = c.play_service_id === value;
              return (
                <button
                  key={c.play_service_id}
                  type="button"
                  onClick={() => {
                    onChange(c.play_service_id);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-surface-sunken',
                    active && 'bg-surface-sunken',
                  )}
                >
                  <Check
                    size={12}
                    className={cn('shrink-0', active ? 'text-accent' : 'opacity-0')}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="truncate font-sans text-[12px] text-text">
                        {c.client_name}
                      </span>
                      <span className="shrink-0 font-mono text-[10px] text-text-faint">
                        {c.currency}
                      </span>
                      {c.partner && (
                        <span className="shrink-0 truncate font-condensed text-[9px] uppercase tracking-wider text-info/70">
                          {c.partner}
                        </span>
                      )}
                      {c.active === 0 && (
                        <span className="shrink-0 font-condensed text-[8px] uppercase tracking-wider text-warn">
                          inactive
                        </span>
                      )}
                    </div>
                    <div className="truncate font-mono text-[10px] text-text-faint">
                      {c.play_service_id}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Message({ children, tone }: { children: React.ReactNode; tone?: 'crit' }) {
  return (
    <div className={cn('px-3 py-3 text-[12px]', tone === 'crit' ? 'text-crit' : 'text-text-dim')}>
      {children}
    </div>
  );
}
